import vsf, { createService, defineStore } from '@vs/vsf-boot';
import Dexie, { Table } from 'dexie';
import { isEmpty } from 'lodash';

class StoreDB extends Dexie {
  Dbenums!: Table<{ data: string; key: string }, number>;
  constructor() {
    super('StoreDB');
    this.version(1).stores({
      Dbenums: 'key',
    });
  }
}

const db = new StoreDB();

export const putDbenums = ({ data, key }) => {
  if (data && key) {
    db.Dbenums.put({
      data: JSON.stringify(data),
      key: `${key}`,
    });
  }
};

export const getDbenums = async ({ key }) => {
  const target = await db.Dbenums.get(key);
  if (target) {
    try {
      const { key, data } = target;
      return {
        key,
        data: JSON.parse(data) ?? [],
      };
    } catch {
      console.log('parse json error !');
    }
  } else {
    return null;
  }
};

export const isNil = (v) => {
  return v === undefined || v === null;
};

export const getMapKeyValue = (data, keyMap) => {
  if (Array.isArray(keyMap)) {
    const result = keyMap
      ?.map((key) => data?.[key])
      ?.filter((v) => v !== undefined);
    return result?.[0];
  } else {
    return data?.[keyMap];
  }
};

const isFalsy = (v) => {
  return v === undefined || v === null;
};

/**
 * 一维对象数组 => 树状结构
 * @param arr
 * @param parentId
 * @param keyMap id parentId 映射
 * @returns
 */
export const buildTree = (
  data,
  keyMap = {
    id: 'id',
    parentId: 'parentId',
  },
) => {
  const tree = {};
  const map = {};
  const otherArr: any[] = [];

  data.forEach((item) => {
    const id = getMapKeyValue(item, keyMap?.id);
    const parentId = getMapKeyValue(item, keyMap?.parentId);
    // const { [keyMap?.id]: id, [keyMap?.parentId]: parentId } = item;
    const isLeaf = false;
    if (!isFalsy(id)) {
      map[id] = { ...item, isLeaf, children: [] };
    } else {
      otherArr.push(item);
    }
  });

  [...Object.values(map), ...otherArr].forEach((item) => {
    const parentId = getMapKeyValue(item, keyMap?.parentId);
    const id = getMapKeyValue(item, keyMap?.id);
    // const parentId = item?.[keyMap?.parentId];
    // 是否有parentId, 且排除自身id跟parentId相同的情况
    const isValidParentId = parentId && parentId !== id;
    if (isValidParentId) {
      if (map[parentId]) {
        map[parentId].children.push(item);
        map[parentId].isLeaf = false;
      } else {
        console.error('找不到父元素');
      }
    } else {
      tree[getMapKeyValue(item, keyMap?.id)] = item;
    }
  });

  return Object.values(tree);
};

const service = createService({
  url: '/api/db_enum/index',
});
const findChildren = (list = [], parent) => {
  return list
    .filter((item: any) => {
      if (
        (isEmpty(item.parentCode) && isEmpty(parent.code)) ||
        item?.parentCode === item?.code
      )
        return false;
      return item.parentCode === parent.code;
    })
    .map((item: any) => ({
      label: item.cname,
      value: item.code,
      children: findChildren(list, item),
      data: item,
    }));
};

export const getData = async (fileMap) => {
  const localData = await getDbenums({
    key: 'data',
  });
  const { map, data } = localData?.data || {};
  const needRequest = fileMap?.filter((item) => {
    const local = (map || [])?.find(
      (litem) => litem?.enumName === item?.enumName,
    );
    return local?.jsonFileUrl !== item?.jsonFileUrl;
  });
  return {
    data: data || {},
    map: needRequest,
  };
};

export const loadEnums = async () => {
  if (Object.keys(vsf.stores.dbenums.enums).length !== 0) return;

  const enumNames = vsf.stores.dbenums.enumNames;
  if (enumNames.length <= 0) return;

  const res = await service();
  if (!res?.data) return;

  const fileMap = res.data as any[];
  const dbData = await getData(fileMap);
  const fileList = enumNames.map((e) => {
    const emap = dbData?.map?.find((f) => f.enumName === e);
    return emap?.jsonFileUrl ?? '';
  });
  const fileSet = new Set<string>(fileList); // 去重
  const loadFilePromises = Array.from(fileSet)
    .filter((f) => !!f)
    .map((url) => {
      const getFileService = createService({ url: url, raw: true });
      return getFileService();
    });
  const files = await Promise.all(loadFilePromises);
  const data = {};
  files.forEach((f) => {
    Object.keys(f).forEach((fkey) => {
      data[fkey] = buildTree(
        f[fkey]
          .filter((d: any) => !d.dbEnumParent) // 一级
          .map((d: any) => ({
            ...d,
            label: d.cname,
            value: d.code,
            children: findChildren(f[fkey], d),
            data: d,
          })),
        {
          id: 'code',
          parentId: 'parentCode',
        },
      );
    });
  });

  await putDbenums({
    key: 'data',
    data: {
      map: fileMap,
      data: {
        ...dbData?.data,
        ...data,
      },
    },
  });

  vsf.stores.dbenums.initEnums({
    ...dbData?.data,
    ...data,
  });
};

const enumNames = {
  //// AUTO GENERATED CODES
  vp2053004: [],
  vp2053005: [],
  vp2053101: [],
  vp2053102: [],
  vp2053103: [],
  vp2053104: [],
  vp2029515: [],
  vp2023901: [],
  //// DO NOT EDIT
};

const dbenums = defineStore(
  {
    // state
    enums: {},
    enumNames: [...new Set(Object.values(enumNames).flat())],

    // action
    initEnums: (data) => (dbenums.enums = data),
  },
  'dbenums',
);

export default dbenums;
